import { useTranslation } from "react-i18next";
import { useEffect, useState, useCallback, useMemo } from "react";
import DeviceData from "./inputs/device_data/DeviceData";
import LivenessPrompt from "./inputs/liveness/LivenessPrompt";
import VideoAgreement from "./inputs/video_agreement/VideoAgreement";
import {
  sendEvent,
  submitVerification,
  safeRedirect,
  reportBillingRefusal,
} from "../store/verification.slice";
import { getHttpErrorMessage } from "../utils/httpErrorMessage";
import UploadError from "./reusable/DocUploadError";
import DocumentPhoto from "./inputs/document_upload/DocumentUpload";
import GovernmentDatabases from "./inputs/government_databases/GovernmentDatabases";
import { useDispatch } from "react-redux";
import EmailCheck from "./inputs/email-check/EmailCheck";
import PhoneCheck from "./inputs/phone-check/PhoneCheck";
import ProofOfResidence from "./inputs/proof_of_residence/ProofOfResidence";
import VerificationInProgress from "./reusable/VerificationInProgress";
import VerificationComplete from "./reusable/VerificationComplete";
import { trackEvent } from "../utils/analytics";

const FieldInput = ({ input, onFieldChange, verification }) => {
  const { t } = useTranslation();
  // Component to render different input types based on field properties
  switch (input?.fieldName) {
    case "DOCUMENT_PHOTO":
      return (
        <DocumentPhoto
          verification={verification}
          key={input?.fieldName}
          field={input?.fieldName}
          inputChange={onFieldChange}
        />
      );
    case "EMAIL_ADDRESS":
      return (
        <EmailCheck
          verification={verification}
          key={input?.fieldName}
          inputChange={onFieldChange}
        />
      );
    case "PHONE_NUMBER":
      return (
        <PhoneCheck
          verification={verification}
          key={input?.fieldName}
          inputChange={onFieldChange}
        />
      );
    case "SELFIE_VIDEO":
      return (
        <LivenessPrompt
          verification={verification}
          key={input?.fieldName}
          inputChange={onFieldChange}
          side={input.fieldName}
          selectedDocument={input.fieldName}
        />
      );
    case "SELFIE_PHOTO":
      return (
        <LivenessPrompt
          verification={verification}
          key={input?.fieldName}
          inputChange={onFieldChange}
          side={input.fieldName}
          selectedDocument={input.fieldName}
        />
      );
    case "DEVICE_DATA":
      return (
        <DeviceData
          verification={verification}
          key={input?.fieldName}
          inputChange={onFieldChange}
          field={input?.fieldName}
        />
      );
    case "VIDEO_AGREEMENT":
      return (
        <VideoAgreement
          verification={verification}
          key={input?.fieldName}
          inputChange={onFieldChange}
          side={input.fieldName}
          selectedDocument={input.fieldName}
        />
      );
    case "GOVERNMENT_CHECK_DATA":
      return (
        <GovernmentDatabases
          verification={verification}
          key={input?.fieldName}
          inputChange={onFieldChange}
          side={input.fieldName}
          selectedDocument={input.fieldName}
        />
      );
    case "PROOF_OF_RESIDENCY_DOC":
      return (
        <ProofOfResidence
          verification={verification}
          key={input?.fieldName}
          inputChange={onFieldChange}
        />
      );
    default:
      return <div>{t("verification.noView")}</div>;
  }
};

function Verification({ verification, redirectUrl, clientId, credentials }) {
  const { t } = useTranslation();
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [documents, setDocuments] = useState({});
  const [formInputs, setFormInputs] = useState({});
  const dispatch = useDispatch();

  // DEVICE_DATA is the only step that can require a page reload mid-flow (see
  // requestLocation in DeviceData.jsx — a denied geolocation permission can only
  // be re-checked by iOS/Android browsers via a fresh navigation, not in place).
  // Running it first means a reload never discards other captured steps, since
  // nothing is persisted to the backend until the final bulk submit below.
  const orderedInputs = useMemo(() => {
    const inputs = verification.inputs || [];
    const deviceData = inputs.filter((input) => input.fieldName === "DEVICE_DATA");
    const rest = inputs.filter((input) => input.fieldName !== "DEVICE_DATA");
    return [...deviceData, ...rest];
  }, [verification.inputs]);

  // Memoized, and updating via the functional form so `documents`/`formInputs`
  // stay out of the dependency list: DeviceData keys its one-shot geolocation
  // bootstrap off this handler's identity, and a new identity per render made it
  // re-query permission and re-submit on every parent render.
  const handleFieldChange = useCallback((values) => {
    const fieldName = orderedInputs[currentFieldIndex]?.fieldName;
    const inputDocuments = [];
    values.forEach((value) => {
      if (value.document != null) {
        inputDocuments.push(value.document);
      }
    });

    setDocuments((prev) => ({ ...prev, [fieldName]: inputDocuments }));

    const fields = values.map((value) => value.field);
    setFormInputs((prev) => ({ ...prev, [fieldName]: fields }));
  }, [orderedInputs, currentFieldIndex]);

  const completeVerification = useCallback(() => {
    const combinedInputs = [].concat(...Object.values(formInputs));
    const combinedDocuments = [].concat(...Object.values(documents));
    setVerifying(true);
    trackEvent("verification_submitted", {
      flowId: verification?.flowId,
      verificationId: verification?.verificationId,
    });

    submitVerification(
      verification?.verificationId,
      combinedDocuments,
      combinedInputs,
      clientId,
      credentials?.accessToken
    )
      .then(() => {
        setIsComplete(true);
        setVerifying(false);
        trackEvent("verification_completed", {
          flowId: verification?.flowId,
          verificationId: verification?.verificationId,
          identityId: verification?.identityId,
        });
        setTimeout(() => {
          dispatch(
            sendEvent({
              type: "verificationComplete",
              details: {
                verificationId: verification?.verificationId,
                identityId: verification?.identityId,
              },
            })
          );
           safeRedirect(redirectUrl, verification?.allowedRedirectUrls, verification?.flowId);
        }, 3000);
      })
      .catch((err) => {
        setIsComplete(true);
        setVerifyError(getHttpErrorMessage(err));
        setVerifying(false);
        trackEvent("verification_submit_failed", {
          flowId: verification?.flowId,
          verificationId: verification?.verificationId,
          error: err + "",
        });
        dispatch(reportBillingRefusal(err, verification?.flowId));
      });
  }, [
    credentials,
    dispatch,
    documents,
    formInputs,
    verification,
    clientId,
    redirectUrl
  ]);

   const goToNextField = useCallback(() => {
    const fieldName = orderedInputs[currentFieldIndex]?.fieldName;
    if (formInputs[fieldName] != null) {
      trackEvent("step_completed", {
        fieldName,
        stepIndex: currentFieldIndex,
        flowId: verification?.flowId,
        verificationId: verification?.verificationId,
      });
      if (currentFieldIndex < orderedInputs.length - 1) {
        setCurrentFieldIndex(currentFieldIndex + 1);
      } else {
        if (!verifying && !isComplete) {
          completeVerification();
        }
      }
    }
  }, [
    completeVerification,
    currentFieldIndex,
    formInputs,
    isComplete,
    orderedInputs,
    verification,
    verifying,
  ]);

  const tryAgain = () => {
    setVerifyError("");
    if (!verifying) {
      completeVerification();
    }
  };

  useEffect(() => {
    goToNextField();
  }, [formInputs, goToNextField]);

  const currentField = orderedInputs[currentFieldIndex];

  useEffect(() => {
    if (currentField?.fieldName) {
      trackEvent("step_started", {
        fieldName: currentField.fieldName,
        stepIndex: currentFieldIndex,
        flowId: verification?.flowId,
        verificationId: verification?.verificationId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentField?.fieldName, currentFieldIndex]);

  return (
    <div className="flex flex-col h-full">
      {!isComplete && !verifying && currentField && (
        <FieldInput
          verification={verification}
          input={currentField}
          onFieldChange={handleFieldChange}
        />
      )}
      {isComplete && !verifyError && !verifying && (
        <VerificationComplete message={t("verification.complete")} />
      )}
      {verifyError && !verifying && (
        <UploadError
          tryAgainFunc={tryAgain}
          title={t("verification.failed")}
          desc={verifyError}
        />
      )}
      {verifying && <VerificationInProgress />}
    </div>
  );
}

export default Verification;
